import { emptyFragment } from '@execonline-inc/execonline-ui';
import { observer } from 'mobx-react';
import * as React from 'react';
import { CompletedProgram, ExpiredProgram } from '../../../ProgramStore/Types';
import { L, T } from '../../../Translations';
import CompletedBadgeGraphic from '../../Cutesies/CompletedBadgeGraphic';
import Linkable from '../../Linkable';
import { ProgramCardPanel } from '.';
import CourseProgressRing from './CourseProgressRing';

type FinishedProgram = CompletedProgram | ExpiredProgram;

interface Props {
  programDetail: FinishedProgram;
}

// A program in the Graduated tab is always graduated. A past program shows the
// graduation details when the learner graduated, and the progress they reached
// before access ended when they did not.
const graduated = (programDetail: FinishedProgram): boolean => {
  switch (programDetail.kind) {
    case 'completed':
      return true;
    case 'expired':
      return programDetail.graduated;
  }
};

const OutcomeGraphic: React.FC<Props> = ({ programDetail }) => {
  switch (programDetail.kind) {
    case 'completed':
      return (
        <div className="w-14 shrink-0">
          <CompletedBadgeGraphic />
        </div>
      );
    case 'expired':
      return programDetail.graduated ? (
        <div className="w-14 shrink-0">
          <CompletedBadgeGraphic />
        </div>
      ) : (
        <CourseProgressRing percentComplete={programDetail.percentComplete} />
      );
  }
};

const AccessDate: React.FC<Props> = ({ programDetail }) => {
  switch (programDetail.kind) {
    case 'completed':
      return programDetail.accessEndsOn
        .map((accessEndsOn) => (
          <p className="m-0 text-sm text-gray-500" data-test-program-access-until={true}>
            <T kind="Program Access Until" />{' '}
            <strong>
              <L localizeable={accessEndsOn} format="long-month-day-year" />
            </strong>
          </p>
        ))
        .getOrElse(emptyFragment);
    case 'expired':
      return programDetail.accessEndsOn
        .orElse(() => programDetail.lockedOutOn)
        .map((accessEndedOn) => (
          <p className="m-0 text-sm text-gray-500" data-test-program-access-ended={true}>
            <T
              kind="Access ended on <date/>"
              date={<L localizeable={accessEndedOn} format="long-month-day-year" />}
            />
          </p>
        ))
        .getOrElse(emptyFragment);
  }
};

const CoachingSessionCount: React.FC<Props> = ({ programDetail }) =>
  programDetail.productDetails
    .map(({ payload }) => {
      switch (payload.kind) {
        case 'coaching':
          return (
            <p className="m-0 text-sm text-gray-500">
              <T
                kind="You have completed total of <sessionCount>{{count}} session(s)</sessionCount> with your coach"
                count={payload.pastSessionsCount}
                sessionCount={(translated) => <strong>{translated}</strong>}
              />
            </p>
          );
        case 'program':
        case 'program-sequence':
        case 'group-coaching':
          return emptyFragment();
      }
    })
    .getOrElse(emptyFragment);

function ProgramOutcomePanel({ programDetail }: Props) {
  return (
    <ProgramCardPanel>
      <OutcomeGraphic programDetail={programDetail} />
      <div className="min-w-0">
        <p className="m-0 text-lg font-semibold text-gray-900">
          <T kind={graduated(programDetail) ? 'Graduated' : 'Progress'} />
        </p>
        {graduated(programDetail) &&
          programDetail.courseCompletedAt
            .map((completedAt) => (
              <p className="m-0 text-gray-600" data-test-program-completed-at={true}>
                <T
                  kind="You graduated from this program on <date/>"
                  date={<L localizeable={completedAt} format="long-month-day-year" />}
                />
              </p>
            ))
            .getOrElse(emptyFragment)}
        <AccessDate programDetail={programDetail} />
        <CoachingSessionCount programDetail={programDetail} />
        {programDetail.linkables
          .map((l) => l.first)
          .map((linkable) => (
            <div className="mt-2">
              <Linkable linkable={linkable} />
            </div>
          ))
          .getOrElse(emptyFragment)}
      </div>
    </ProgramCardPanel>
  );
}

export default observer(ProgramOutcomePanel);
